import React, { useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  Layout as AntLayout,
  Menu,
  Button,
  Dropdown,
  Avatar,
  Space,
  Typography,
  message
} from 'antd'
import {
  DashboardOutlined,
  MessageOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  BarChartOutlined
} from '@ant-design/icons'
import { useAuthStore } from '@/stores/authStore'
import { authApi } from '@/services/api'

const { Header, Sider, Content, Footer } = AntLayout
const { Text } = Typography

const Layout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuthStore()

  const handleLogout = async () => {
    try {
      await authApi.logout()
      logout()
      message.success('登出成功')
      navigate('/login')
    } catch (error) {
      console.error('登出失败:', error)
      logout()
      navigate('/login')
    }
  }

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: '仪表板',
    },
    {
      key: 'conversations',
      icon: <MessageOutlined />,
      label: '会话管理',
      children: [
        {
          key: '/conversations/latest',
          label: '最新会话',
        },
        {
          key: '/conversations/chat',
          label: '聊天管理',
        },
      ],
    },
    {
      key: 'analytics',
      icon: <BarChartOutlined />,
      label: '数据分析',
      children: [
        {
          key: '/analytics/models',
          label: '模型使用率',
        },
        {
          key: '/analytics/billing',
          label: 'Token计费',
        },
        {
          key: '/analytics/costs',
          label: '成本分析',
        },
        {
          key: '/analytics/users',
          label: '用户行为分析',
        },
      ],
    },
    {
      key: 'monitoring',
      icon: <SettingOutlined />,
      label: '接口监控',
      children: [
        {
          key: '/monitoring/proxy',
          label: 'Proxy状态',
        },
        {
          key: '/monitoring/logs',
          label: '日志管理',
        },
        {
          key: '/monitoring/errors',
          label: '错误监控',
        },
        {
          key: '/monitoring/realtime',
          label: '实时监控',
        },
      ],
    },
    {
      key: 'models',
      icon: <SettingOutlined />,
      label: '模型配置',
      children: [
        {
          key: '/models/pricing',
          label: '模型价格',
        },
        {
          key: '/models/management',
          label: '模型管理',
        },
      ],
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '系统设置',
      children: [
        {
          key: '/settings/proxy',
          label: '代理设置',
        },
      ],
    },
  ]

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      handleLogout()
    } else {
      navigate(`/${key}`)
    }
  }

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人信息',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '设置',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ]

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key)
  }

  return (
    <AntLayout className="admin-layout">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        className="admin-sider"
        width={200}
      >
        <div style={{ 
          height: 64, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <Text strong style={{ color: '#fff', fontSize: collapsed ? 14 : 16 }}>
            {collapsed ? 'RA' : 'Refly Admin'}
          </Text>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          defaultOpenKeys={['conversations', 'analytics', 'monitoring', 'models', 'settings']}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ borderRight: 0 }}
        />
      </Sider>
      
      <AntLayout style={{ marginLeft: collapsed ? 80 : 200, transition: 'margin-left 0.2s' }}>
        <Header className="admin-header" style={{ padding: '0 24px' }}>
          <div className="flex-between">
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: 16, width: 64, height: 64 }}
            />
            
            <Space>
              <Text type="secondary">欢迎回来，</Text>
              <Dropdown
                menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
                placement="bottomRight"
                trigger={['click']}
              >
                <Space style={{ cursor: 'pointer' }}>
                  <Avatar icon={<UserOutlined />} />
                  <Text strong>{user?.username}</Text>
                </Space>
              </Dropdown>
            </Space>
          </div>
        </Header>
        
        <Content className="admin-content" style={{ padding: 24 }}>
          <Outlet />
        </Content>
        
        <Footer className="admin-footer">
          Refly Admin System ©2025 Created by Refly Team
        </Footer>
      </AntLayout>
    </AntLayout>
  )
}

export default Layout
